/**
 * Per-ISEC-question documentation rows — checkbox + question text + free-form
 * note beside each point (OCP round-2 feedback). Rendered by BOTH the Clinical
 * Verification sign page and the Counseling dialog so the layout is exactly
 * the same on the two surfaces. Presentation-only: state updates go through
 * the shared helpers below so the GP-926 sticky auto-check behaves identically
 * everywhere (typing a note checks the point; clearing it does not un-check).
 */
import React from 'react';
import { Box, Checkbox, TextField, Typography } from '@mui/material';
import { ISEC_POINT_IDS } from '../../constants/isecQuestions';
import type { IsecPoints, IsecQuestion } from '../../constants/isecQuestions';

/** Toggle a point's checkbox. */
export function toggleIsecPoint(points: IsecPoints, id: string): IsecPoints {
  const item = points[id] || { checked: false, notes: '' };
  return { ...points, [id]: { ...item, checked: !item.checked } };
}

/**
 * PRX-207: tick every ISEC point at once, keeping each point's note. Points
 * stay individually toggleable afterwards.
 */
export function checkAllIsecPoints(points: IsecPoints): IsecPoints {
  return {
    ...points,
    ...Object.fromEntries(
      ISEC_POINT_IDS.map((id) => [id, { notes: points[id]?.notes ?? '', checked: true }]),
    ),
  };
}

/** Set a point's note — sticky auto-check (GP-926). */
export function setIsecPointNotes(points: IsecPoints, id: string, notes: string): IsecPoints {
  const item = points[id] || { checked: false, notes: '' };
  return { ...points, [id]: { notes, checked: item.checked || notes.trim().length > 0 } };
}

interface IsecPointRowsProps {
  questions: IsecQuestion[];
  points: IsecPoints;
  onToggle: (id: string) => void;
  onNotes: (id: string, notes: string) => void;
  readOnly?: boolean;
}

const IsecPointRows: React.FC<IsecPointRowsProps> = ({
  questions,
  points,
  onToggle,
  onNotes,
  readOnly,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    {questions.map((q) => {
      const item = points[q.id] || { checked: false, notes: '' };
      return (
        <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5 }}>
          <Checkbox
            size="small"
            checked={item.checked}
            onChange={() => onToggle(q.id)}
            disabled={readOnly}
            sx={{ p: 0.5 }}
          />
          <Typography
            variant="body2"
            sx={{ flex: 1, fontSize: '0.78rem', fontWeight: item.checked ? 600 : 400 }}
          >
            {q.text}
          </Typography>
          <TextField
            size="small"
            placeholder="Notes..."
            value={item.notes}
            onChange={(e) => onNotes(q.id, e.target.value)}
            disabled={readOnly}
            multiline
            maxRows={6}
            sx={{
              width: 220,
              // Grows while typing (multiline) and widens on focus for longer notes.
              transition: 'width 0.15s ease',
              '&:focus-within': { width: 340 },
              bgcolor: item.checked ? '#e0f7fa' : undefined,
              '& .MuiInputBase-root': { py: 0.5 },
              '& .MuiInputBase-input': { fontSize: '0.75rem', lineHeight: 1.35 },
            }}
          />
        </Box>
      );
    })}
  </Box>
);

export default IsecPointRows;
